"use client";

import React from "react";
import { getEntityDefinition, getEntityTableConfig } from "@/lib/entity-registry";
import { TicketDetailTabComponents } from "@/features/tickets/components/TicketDetailTabComponents";
import { TicketGlanceCard } from "@/features/tickets/components/TicketGlanceCard";
import { InlineEditableInfoPanel } from "@/components/shared/InlineEditableInfoPanel";
import type { Tickets } from "@/src/gql/graphql";
import type {
  TabConfig,
  TabContentProps,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Ticket } from "@/lib/types/database";

// ============================================================================
// TICKET DETAIL MODAL TABS HOOK
// ============================================================================
//
// Sources tabs from the SAME registry detail config the ticket detail PAGE
// uses (ENHANCED_TICKET_DETAIL_CONFIG), and dispatches each tab through the
// shared TicketDetailTabComponents map by its `customComponent` key. This
// mirrors useContactDetailModalTabs, giving the modal full parity with the
// page: rich Contact tab, Company tab, Tasks, Attachments — no hand-rolled
// tab markup to drift out of sync.
//
// Tabs are display-only in the modal (no TicketEditModeProvider here), exactly
// like the contact modal. Inline editing remains on the detail page.

type TicketTabComponent = React.ComponentType<TabContentProps<Ticket>>;

const TICKET_TAB_COMPONENTS = TicketDetailTabComponents as Record<
  string,
  TicketTabComponent
>;

function resolveTabComponent(
  tab: TabConfig<Tickets> | undefined
): TicketTabComponent {
  const custom = tab?.content?.customComponent;
  if (typeof custom === "string" && TICKET_TAB_COMPONENTS[custom]) {
    return TICKET_TAB_COMPONENTS[custom];
  }
  // Fallback to the Details tab component for any unmapped/unknown tab.
  return TICKET_TAB_COMPONENTS.TicketDetails;
}

export interface UseTicketDetailModalTabsReturn {
  tabs: TabConfig<Tickets>[];
  renderPrimaryArea: (activeTab: string, ticket: Tickets) => React.ReactNode;
  renderInfoPanel: (ticket: Tickets) => React.ReactNode;
  renderInlineInfoPanel: (
    ticket: Tickets,
    onInlineEdit: (ticket: Tickets) => Promise<void>
  ) => React.ReactNode;
}

export function useTicketDetailModalTabs(): UseTicketDetailModalTabsReturn {
  // Single source of truth: the registry detail config (same as the page).
  const tabs = getEntityDefinition("tickets").detailConfig
    .tabs as unknown as TabConfig<Tickets>[];

  // Render primary area by dispatching to the registered tab component.
  const renderPrimaryArea = React.useCallback((
    activeTab: string,
    ticket: Tickets
  ): React.ReactNode => {
    const tabConfig = tabs.find((t) => t.id === activeTab) ?? tabs[0];
    const TabComponent = resolveTabComponent(tabConfig);
    return (
      <TabComponent item={ticket} tab={tabConfig} />
    );
  }, [tabs]);

  // High-level "at a glance" card — read-only, surfaces status/priority, client,
  // SLA/due pressure, and time totals for fast triage.
  const renderGlance = React.useCallback((ticket: Tickets): React.ReactNode => (
    <TicketGlanceCard ticket={ticket} />
  ), []);

  // Fallback (no inline edit available): glance card only.
  const renderInfoPanel = React.useCallback((ticket: Tickets): React.ReactNode => (
    <div className="space-y-4">{renderGlance(ticket)}</div>
  ), [renderGlance]);

  // Default sidebar in the modal/page: the glance card above the full
  // inline-editable field panel sourced from the ticket table config.
  const ticketTableConfig = getEntityTableConfig("tickets");
  const renderInlineInfoPanel = React.useCallback((
    ticket: Tickets,
    onInlineEdit: (ticket: Tickets) => Promise<void>
  ): React.ReactNode => (
    <div className="space-y-4">
      {renderGlance(ticket)}
      <InlineEditableInfoPanel
        item={ticket}
        config={ticketTableConfig as never}
        onInlineEdit={onInlineEdit}
        title="Ticket details"
        showExpandButton
        variant="panel"
      />
    </div>
  ), [renderGlance, ticketTableConfig]);

  return React.useMemo(
    () => ({
      tabs,
      renderPrimaryArea,
      renderInfoPanel,
      renderInlineInfoPanel,
    }),
    [tabs, renderPrimaryArea, renderInfoPanel, renderInlineInfoPanel],
  );
}
